// alterIndex — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'index-lifecycle', lifecycle })` op (DSE2 §3.C5).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice C5 — Index lifecycle UI.
//
// Per-engine support (mirrors DSE2's `IndexLifecycleOp` reject paths):
//   - mssql:    rebuild, reorganize, swap (clustered-swap)
//   - postgres: rebuild (REINDEX INDEX), reindex (target=index|table)
//   - mysql:    none surfaced per-row — mysql's lifecycle commands are
//               table-level (`ALTER TABLE … ENGINE = InnoDB`,
//               `OPTIMIZE TABLE …`); a per-index action would mislead.
//               The IndexesTab renders the affordance disabled with the
//               documented tooltip pointing at the table-level idiom.
//   - oracle:   rebuild, reorganize (`ALTER INDEX … COALESCE`)
//   - sqlite:   reindex (sqlite has no separate rebuild — `rebuild` and
//               `reindex` would emit identical SQL; the menu surfaces only
//               `reindex` to avoid duplicate options.)
//
// `swap` (clustered-swap) is mssql-only and uses the row's existing
// IndexDef.columns + name + table to emit `CREATE [UNIQUE] [CLUSTERED]
// INDEX … WITH (DROP_EXISTING = ON)`. The `clustered-swap` shape can also
// rebuild a non-clustered index in place via `clustered: false`.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  IndexLifecycleOp,
} from '../../../../sql-resolver/types/ddlEdit';
import type { QualifiedName } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

/** Surface-level action name (PRD shorthand). Mapped to DSE2's
 *  `IndexLifecycleOp.kind` via `toLifecycleOp` — `'swap'` becomes
 *  `'clustered-swap'`. */
export type AlterIndexAction = 'rebuild' | 'reorganize' | 'reindex' | 'swap';

export type AlterIndexArgs = {
  readonly action: AlterIndexAction;
  /** Index target — required for every action except mysql rebuild
   *  (which is table-level; mysql is not surfaced in the row-action menu
   *  anyway). */
  readonly indexName?: QualifiedName;
  /** Owning table — required on mssql rebuild/reorganize/swap. Optional
   *  on pgsql / oracle / sqlite. */
  readonly table?: QualifiedName;
  /** mssql-only verbatim `WITH (...)` options text — e.g.
   *  `'ONLINE = ON, MAXDOP = 4'`. The synthesizer wraps it. */
  readonly mssqlOptions?: string;
  /** oracle-only: append `ONLINE` to REBUILD. */
  readonly oracleOnline?: boolean;
  /** oracle-only: append `TABLESPACE <name>` to REBUILD. */
  readonly oracleTablespace?: string;
  /** Reindex target (pgsql distinguishes INDEX vs TABLE; sqlite accepts
   *  either name in the same syntactic slot). Defaults to `'index'`. */
  readonly reindexTarget?: 'index' | 'table';
  /** Clustered-swap key column list — required for `action: 'swap'`. The
   *  caller passes the row's existing `IndexDef.columns` to rebuild in
   *  place, or new columns to change the clustered key. */
  readonly columns?: readonly string[];
  /** Clustered-swap: emit `CREATE CLUSTERED INDEX` (default `true`) or a
   *  non-clustered swap (`false`). */
  readonly clustered?: boolean;
  /** Clustered-swap: emit `CREATE UNIQUE [CLUSTERED] INDEX`. */
  readonly unique?: boolean;
};

function toLifecycleOp(args: AlterIndexArgs): IndexLifecycleOp {
  switch (args.action) {
    case 'rebuild':
      return {
        kind: 'rebuild',
        indexName: args.indexName,
        table: args.table,
        mssqlOptions: args.mssqlOptions,
        oracleOnline: args.oracleOnline,
        oracleTablespace: args.oracleTablespace,
      };
    case 'reorganize':
      return {
        kind: 'reorganize',
        indexName: args.indexName,
        table: args.table,
      };
    case 'reindex':
      return {
        kind: 'reindex',
        target: args.reindexTarget ?? 'index',
        name: requireName(args.indexName),
      };
    case 'swap':
      return {
        kind: 'clustered-swap',
        indexName: requireName(args.indexName),
        table: requireName(args.table),
        columns: args.columns ?? [],
        clustered: args.clustered,
        unique: args.unique,
      };
  }
}

function requireName(qn: QualifiedName | undefined): QualifiedName {
  if (qn === undefined) {
    throw new Error('alterIndex: a target QualifiedName is required for this action.');
  }
  return qn;
}

export function buildAlterIndexEdit(args: AlterIndexArgs): DdlEdit {
  return { op: 'index-lifecycle', lifecycle: toLifecycleOp(args) };
}

export async function commitAlterIndex(
  ctx: VqeDdlEditContext,
  args: AlterIndexArgs,
): Promise<void> {
  const edit = buildAlterIndexEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

/** Per-engine surfaced action set. mysql intentionally returns `[]` — its
 *  lifecycle commands are table-level (`ALTER TABLE … ENGINE = InnoDB`,
 *  `OPTIMIZE TABLE …`); the per-row affordance disables on mysql with a
 *  documented tooltip. sqlite returns only `'reindex'` — `'rebuild'` would
 *  emit identical SQL on sqlite (DSE2 aliases them). */
const ENGINE_ACTIONS: Record<SqlEngine, readonly AlterIndexAction[]> = {
  mssql: ['rebuild', 'reorganize', 'swap'],
  postgres: ['rebuild', 'reindex'],
  mysql: [],
  oracle: ['rebuild', 'reorganize'],
  sqlite: ['reindex'],
};

export function getAvailableLifecycleActions(
  engine: SqlEngine,
): readonly AlterIndexAction[] {
  return ENGINE_ACTIONS[engine];
}

export function isAlterIndexSupported(engine: SqlEngine): boolean {
  return ENGINE_ACTIONS[engine].length > 0;
}

export function isLifecycleActionSupported(
  engine: SqlEngine,
  action: AlterIndexAction,
): boolean {
  return ENGINE_ACTIONS[engine].includes(action);
}

export function getAlterIndexUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL has no per-index lifecycle commands. Use ALTER TABLE … ENGINE '
      + '= InnoDB to rebuild the entire table, or OPTIMIZE TABLE for the '
      + 'reorganize equivalent — both operate on the whole table, not a '
      + 'single index.'
    );
  }
  return '';
}

const ACTION_LABELS: Record<AlterIndexAction, string> = {
  rebuild: 'Rebuild',
  reorganize: 'Reorganize',
  reindex: 'Reindex',
  swap: 'Swap (DROP_EXISTING)',
};

export function getLifecycleActionLabel(action: AlterIndexAction): string {
  return ACTION_LABELS[action];
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths so the
 *  caller can surface user-friendly errors before the synthesizer would
 *  throw. Returns `null` when the args are acceptable. */
export function validateAlterIndexForEngine(
  engine: SqlEngine,
  args: AlterIndexArgs,
): string | null {
  if (!isAlterIndexSupported(engine)) {
    return getAlterIndexUnsupportedTooltip(engine);
  }
  if (!isLifecycleActionSupported(engine, args.action)) {
    const supported = ENGINE_ACTIONS[engine].join(', ');
    return (
      `${engine} does not support index lifecycle action '${args.action}'. `
      + `Supported on ${engine}: ${supported}.`
    );
  }
  if (args.action === 'swap') {
    if (args.columns === undefined || args.columns.length === 0) {
      return 'Clustered swap requires at least one key column.';
    }
  }
  if (args.mssqlOptions !== undefined && engine !== 'mssql') {
    return `${engine} does not accept WITH options on ALTER INDEX — mssql-only field.`;
  }
  if ((args.oracleOnline === true || args.oracleTablespace !== undefined) && engine !== 'oracle') {
    return `${engine} does not accept ONLINE / TABLESPACE on ALTER INDEX — oracle-only fields.`;
  }
  return null;
}
